/**
 * Design System — EstoqueLocal Scale
 * Paleta de confeitaria: tons quentes, femininos, profissionais.
 */

export const theme = {
  colors: {
    primary: '#C4786E',
    primaryLight: '#F5E6E3',
    primaryDark: '#A35D54',
    secondary: '#8B6F5E',
    accent: '#E8B4A0',

    bg: '#FFFAF8',
    bgCard: '#FFFFFF',
    bgSection: '#FDF7F4',

    text: '#3D2C2C',
    textSecondary: '#8C7A7A',
    textMuted: '#BBA9A9',

    border: '#F0E6E2',
    borderLight: '#F7EFEC',

    success: '#5DAE7B',
    successLight: '#EDF7F0',
    warning: '#E8A85C',
    warningLight: '#FDF5EC',
    danger: '#D46B6B',
    dangerLight: '#FBEAEA',
    info: '#7BA3C4',
    infoLight: '#EDF3F9',
  },

  radius: { sm: 8, md: 12, lg: 16, xl: 20, pill: 9999 },

  shadow: {
    sm: '0 1px 3px rgba(61,44,44,0.06)',
    md: '0 4px 16px rgba(61,44,44,0.08)',
    lg: '0 12px 40px rgba(61,44,44,0.12)',
    xl: '0 20px 60px rgba(61,44,44,0.16)',
  },

  font: "'Inter', 'Segoe UI', -apple-system, sans-serif",

  // Spacing scale
  space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 },

  // Navbar
  sidebar: { width: 240, collapsedWidth: 64 },
};

// Inline style helpers
export const t = {
  // Buttons
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 20px', borderRadius: 10, border: 'none',
    background: theme.colors.primary, color: '#fff',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  },
  btnSecondary: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 20px', borderRadius: 10,
    border: `1.5px solid ${theme.colors.border}`,
    background: '#fff', color: theme.colors.text,
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
    transition: 'all 0.15s',
  },
  btnGhost: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', borderRadius: 8, border: 'none',
    background: 'transparent', color: theme.colors.textSecondary,
    fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
  },
  btnSmall: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '6px 14px', borderRadius: 8, border: 'none',
    background: theme.colors.primary, color: '#fff',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnDanger: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', borderRadius: 8, border: 'none',
    background: theme.colors.dangerLight, color: theme.colors.danger,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6, border: 'none',
    background: 'transparent', color: 'inherit',
    fontSize: 14, cursor: 'pointer', padding: 0,
  },

  // Inputs
  input: {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: `1.5px solid ${theme.colors.border}`,
    fontSize: 14, color: theme.colors.text, outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.2s',
    fontFamily: theme.font,
  },
  select: {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: `1.5px solid ${theme.colors.border}`,
    fontSize: 14, color: theme.colors.text, outline: 'none',
    boxSizing: 'border-box', background: '#fff',
    fontFamily: theme.font, cursor: 'pointer',
  },
  label: {
    display: 'block', fontSize: 13, fontWeight: 600,
    color: theme.colors.textSecondary, marginBottom: 6,
    letterSpacing: '0.01em',
  },

  // Layout
  card: {
    background: theme.colors.bgCard, borderRadius: 16,
    border: `1px solid ${theme.colors.borderLight}`,
    boxShadow: theme.shadow.sm,
  },
  section: {
    background: theme.colors.bgSection, borderRadius: 14,
    padding: '16px 20px',
  },

  // Modal
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(61,44,44,0.3)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, overflow: 'auto', padding: 12,
  },
  modal: {
    background: '#fff', borderRadius: 20,
    padding: '24px', maxHeight: '90vh', overflow: 'auto',
    boxShadow: theme.shadow.xl,
    width: '100%', maxWidth: 480,
  },

  // Table
  th: {
    padding: '10px 14px', textAlign: 'left', fontWeight: 600,
    fontSize: 12, color: theme.colors.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: `1.5px solid ${theme.colors.border}`,
  },
  td: {
    padding: '12px 14px', fontSize: 14, color: theme.colors.text,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },

  // Typography
  h1: { fontSize: 24, fontWeight: 700, color: theme.colors.text, margin: 0, letterSpacing: '-0.02em' },
  h2: { fontSize: 20, fontWeight: 700, color: theme.colors.text, margin: 0, letterSpacing: '-0.01em' },
  h3: { fontSize: 16, fontWeight: 600, color: theme.colors.text, margin: 0 },
  subtitle: { fontSize: 14, color: theme.colors.textSecondary, margin: '4px 0 0' },

  // Badge
  badge: (color, bgColor) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 600, color, background: bgColor,
  }),

  // Status
  error: {
    background: theme.colors.dangerLight, color: theme.colors.danger,
    padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13,
  },
  success: {
    background: theme.colors.successLight, color: theme.colors.success,
    padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13,
  },
};

export default theme;
